/**
 * `saveBlobAsFile` — the ONE place a fetched blob becomes a file on disk.
 *
 * Task 306A §3. Task file: `tasks/306A-commercial-ux-closure.md`.
 *
 * ---------------------------------------------------------------------
 * WHY THIS IS A MODULE AND NOT FOUR LINES INSIDE A PAGE.
 *
 * The contract PDF download shipped as four lines inside
 * `ClientContractPage`, and it was wrong in a way no test could see:
 * every assertion about it was a SOURCE-STRING pin, so the path was
 * described but never executed. The saver is here, exported, and its
 * timers and listeners are injectable, so the lifecycle below is proved
 * by running it.
 *
 * THE DEFECT THIS FIXES, STATED PLAINLY. The previous saver did:
 *
 *     anchor.click();
 *     anchor.remove();
 *     window.setTimeout(() => URL.revokeObjectURL(objectUrl), 250);
 *
 * A `blob:` download is served FROM the blob the URL resolves to. Revoking
 * that URL while the browser is still reading it terminates the transfer —
 * Chromium reports a failed download, and the page never learns anything,
 * because from JavaScript's point of view the click succeeded. The old
 * code's own comment named the hazard exactly ("with 'ask where to save'
 * enabled a large blob's save dialog has not resolved by the next tick,
 * and revoking early cancels the download") and then picked 250 ms, which
 * is shorter than a human choosing a folder and shorter than committing
 * 25 MiB to a slow volume. The comment and the constant disagreed; the
 * constant is what shipped.
 *
 * ---------------------------------------------------------------------
 * COMPLETION IS UNKNOWABLE FROM JAVASCRIPT, AND THIS MODULE SAYS SO.
 *
 * There is no event for "the download finished". So a bigger number is
 * not a fix: a binding review pointed out that a bare 60-second timer is
 * the same race as the 250 ms one, just longer — a user who leaves a
 * "where do you want to save this" dialog open for a minute still gets
 * the URL revoked underneath them.
 *
 * So the fallback timer NEVER RUNS WHILE THE PAGE DOES NOT HAVE FOCUS.
 * A native save dialog takes focus away from the document, which is the
 * one observable signal that correlates with "the user has not resolved
 * this download yet":
 *
 *   - `blur`     → disarm. Whatever is holding focus (a save dialog, the
 *                  file manager, another app) is not this page, and the
 *                  transfer may still be pending.
 *   - `focus`    → re-arm from zero. The dialog is gone; start counting
 *                  again.
 *   - `pagehide` → revoke NOW. The document is going away, so nothing can
 *                  still be reading the blob and holding it only leaks.
 *
 * The residual, stated honestly rather than papered over: a save that is
 * still writing 60 seconds after the page regained focus would still be
 * cut. Nothing client-side can rule that out, because nothing client-side
 * can observe completion. What CAN be ruled out is the case that actually
 * bites — an open dialog — and that is what the focus gate does.
 *
 * `visibilitychange` is deliberately NOT used. Switching tabs is ordinary
 * and says nothing about the download; focus is the signal a modal dialog
 * moves, and tab-switching moves it too, so `blur` already covers the
 * cases `visibilitychange` would have.
 *
 * IT DOES NOT OPEN A TAB. `window.open` after an `await` is outside the
 * user-gesture stack and Safari blocks it routinely; its null return was
 * unchecked, so a blocked popup was a silent no-op (303-A §2.10 also
 * forbids putting a storage key in the address bar, which is what the
 * signed-URL version did).
 * ---------------------------------------------------------------------
 */

/**
 * How long an object URL may outlive its click, COUNTED ONLY WHILE THE
 * DOCUMENT HAS FOCUS.
 *
 * A ceiling on a leak for a tab that stays open and focused, not a guess
 * at how long a download takes. See the header for why a bare elapsed-time
 * revoke is the original defect wearing a bigger number.
 */
export const OBJECT_URL_FALLBACK_REVOKE_MS = 60_000;

/** The window events the saver listens to. */
export type SaveBlobEvent = 'pagehide' | 'blur' | 'focus';

/** Everything the saver touches, injectable so tests execute the real path. */
export interface SaveBlobEnvironment {
  readonly createObjectURL: (blob: Blob) => string;
  readonly revokeObjectURL: (url: string) => void;
  readonly document: Pick<Document, 'createElement' | 'body'>;
  readonly addEventListener: (type: SaveBlobEvent, listener: () => void) => void;
  readonly removeEventListener: (type: SaveBlobEvent, listener: () => void) => void;
  readonly setTimeout: (handler: () => void, ms: number) => number;
  readonly clearTimeout: (handle: number) => void;
  /** False while a native save dialog (or anything else) holds focus. */
  readonly hasFocus: () => boolean;
}

function browserEnvironment(): SaveBlobEnvironment {
  return {
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
    document: window.document,
    addEventListener: (type, listener) => window.addEventListener(type, listener),
    removeEventListener: (type, listener) => window.removeEventListener(type, listener),
    setTimeout: (handler, ms) => window.setTimeout(handler, ms),
    clearTimeout: (handle) => window.clearTimeout(handle),
    hasFocus: () => window.document.hasFocus(),
  };
}

/**
 * Hand `blob` to the browser's download manager under `filename`.
 *
 * Returns the object URL it created — for tests and for callers that want
 * to assert the lifecycle. Callers must NOT revoke it: this function owns
 * the URL from creation to revocation, and a second revoke from outside is
 * exactly the defect described in the header.
 */
export function saveBlobAsFile(
  blob: Blob,
  filename: string,
  env: SaveBlobEnvironment = browserEnvironment(),
): string {
  const objectUrl = env.createObjectURL(blob);

  const anchor = env.document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  // Firefox requires the anchor to be in the document for a programmatic
  // click to dispatch. It is removed immediately afterwards; the download
  // does not depend on the element surviving, only on the URL.
  env.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  // Every binding is declared BEFORE any closure reads it. A fake clock
  // that fires its handler synchronously would otherwise hit the temporal
  // dead zone of a `const` declared later — a crash reachable only from a
  // test, which is the worst place to discover one.
  let timer: number | null = null;
  let released = false;

  const disarm = () => {
    if (timer !== null) {
      env.clearTimeout(timer);
      timer = null;
    }
  };

  const release = () => {
    if (released) return;
    released = true;
    disarm();
    env.removeEventListener('pagehide', release);
    env.removeEventListener('blur', disarm);
    env.removeEventListener('focus', arm);
    env.revokeObjectURL(objectUrl);
  };

  // Named `function` rather than `const`: `release` above references it,
  // and a hoisted declaration is the only form that is defined by then.
  function arm(): void {
    if (released) return;
    disarm();
    timer = env.setTimeout(release, OBJECT_URL_FALLBACK_REVOKE_MS);
  }

  env.addEventListener('pagehide', release);
  env.addEventListener('blur', disarm);
  env.addEventListener('focus', arm);

  // Armed only if the page actually has focus right now. A click that is
  // immediately followed by a native dialog may already have lost it.
  if (env.hasFocus()) arm();

  return objectUrl;
}
